import React from 'react';

import {AnalyticsVisualizationBarChartType as BarChartType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';

import {VisualizationType} from '../AnalyticsVisualizationTypes';
import AggregationConfigEditor from './config-components/AggregationConfigEditor';
import AxisRangeConfigEditor from './config-components/AxisRangeConfigEditor';
import BarChartTypeConfigEditor from './config-components/BarChartTypeConfigEditor';
import ErrorBarConfigEditor from './config-components/ErrorBarConfigEditor';
import XYSeriesColumnEditor from './config-components/XYSeriesColumnEditor';
import TabbedConfigEditorSkeleton from './TabbedConfigEditorSkeleton';
import {VisualizationConfigEditorComponentProps} from './VisualizationConfigEditorTypes';

type BarChartParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationBarChartParametersNode'
>;

export type BarChartConfigEditorProps = VisualizationConfigEditorComponentProps<BarChartParameters>;

export const STACKED_BAR_CHART_ERROR_BAR_WARNING =
  'Error bars can’t be displayed on stacked bar charts. The error calculation will still appear in the chart tooltip.';

export default function BarChartConfigEditor(props: BarChartConfigEditorProps): JSX.Element {
  const {
    onUpdateParameters,
    parameters,
    visualizationConfig: {id: visualizationConfigId},
    enableAnalysisTab,
    enableFormatTab,
    enableAggregation = true,
  } = props;

  return (
    <>
      <BarChartTypeConfigEditor {...props} />
      <TabbedConfigEditorSkeleton
        setupTabContent={<XYSeriesColumnEditor {...props} isSortable={true} />}
        analysisTabContent={
          <>
            {enableAggregation && (
              <AggregationConfigEditor parameters={parameters} onUpdateParameters={onUpdateParameters} />
            )}
            <ErrorBarConfigEditor
              parameters={parameters}
              onUpdateParameters={onUpdateParameters}
              warning={
                parameters.barChartType === BarChartType.STACKED ? STACKED_BAR_CHART_ERROR_BAR_WARNING : null
              }
            />
          </>
        }
        formatTabContent={
          <AxisRangeConfigEditor<BarChartParameters, 'yAxisMin', 'yAxisMax'>
            axisLabel="Y-Axis Range"
            minKey="yAxisMin"
            maxKey="yAxisMax"
            parameters={parameters}
            onUpdateParameters={onUpdateParameters}
          />
        }
        enableAnalysisTab={enableAnalysisTab}
        enableFormatTab={enableFormatTab}
        visualizationConfigId={visualizationConfigId}
        visualizationType={VisualizationType.BAR_CHART}
      />
    </>
  );
}
